// ReadinessCard — standalone readiness UI rendered in two places:
//   1. AI Coach → Insights (HayahAIInsights)
//   2. Exercise → Today (Riyadah TodayPanel) — pre-flight before user taps
//      Start Workout, so the recommendation is in front of them at the
//      moment of intent.
//
// Self-contained: owns its own loading/error state, generation call,
// cache load, and the Steve toggle. Parent just <ReadinessCard />.
//
// Visual chrome lives here so both surfaces stay identical — change the
// card once, both places update.

import React, { useCallback, useEffect, useState, useRef } from "react";
import { registerPlugin } from "@capacitor/core";
import { getGeminiKey } from "../geminiAI";
import { readAllData } from "./aiContext";
import { hapticTap, hapticHayahToggle } from "../../hayahHaptics";
import { loadReadinessCache, loadReadinessHistory, recordFeel } from "./readinessCache";
import { analyzeFeelGap, preEmptiveFeelHint } from "./readinessLearning";
import { generateReadiness } from "./readinessAI";
import { runJob, subscribe, isInFlight, lastError } from "./aiBackgroundJob";
import { computeReadinessInputs } from "./readiness";
import { computeCardioFitness } from "./cardioFitness";
import { computeRegionReadiness } from "./muscleReadiness";
import { capByFeel, FEEL_OPTIONS, loadFeel, saveFeel } from "./readinessScore";
import { C, FS, FM } from "../HayahToday";
import AICostBadge from "./AICostBadge";
import AIAnalysingPipeline from "./AIAnalysingPipeline";
import { getDriverExplanation } from "./driverGlossary";
import { openIOSSettings } from "../../dictate";
import { fireConfounderAlertIfNew, initReadinessAlerts } from "./readinessAlerts";

const GhidhaHealth = registerPlugin("GhidhaHealth");

// Pulls just the signals readiness scores on (HRV, RHR, sleep) from
// HealthKit and writes them into today's `calorieLog` entry so the next
// readAllData() snapshot reflects the user's current state — not whatever
// was synced at app launch. Native-only; resolves to false on web.
async function syncReadinessSignals() {
  if (!window.Capacitor?.isNativePlatform?.()) return false;
  try {
    const todayKey = (() => {
      const n = new Date();
      const y = n.getFullYear();
      const m = String(n.getMonth() + 1).padStart(2, "0");
      const d = String(n.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    })();

    let rhr = 0, hrv = 0;
    let rhrAt = null, hrvAt = null;
    let rhrFresh = false, hrvFresh = false;
    try {
      const a = await GhidhaHealth.readActivityData();
      rhr = Math.round(a?.restingHeartRate     || 0);
      hrv = Math.round(a?.heartRateVariability || 0);
      rhrAt = a?.restingHeartRateAt     || null;
      hrvAt = a?.heartRateVariabilityAt || null;
      const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
      rhrFresh = !!(rhrAt && Date.parse(rhrAt) >= todayStart.getTime());
      hrvFresh = !!(hrvAt && Date.parse(hrvAt) >= todayStart.getTime());
    } catch {}

    let sleepMinutes = 0, deepMinutes = 0, remMinutes = 0, awakeMinutes = 0;
    try {
      const r = await GhidhaHealth.readSleepData();
      const samples = r?.data || [];
      const hasDetailed = samples.some(s =>
        s.sleepState === "ASLEEPCORE" || s.sleepState === "ASLEEPDEEP" || s.sleepState === "ASLEEPREM");
      const isDeep = s => /deep/i.test(String(s));
      const isREM  = s => /rem/i.test(String(s));
      for (const s of samples) {
        const st = s.sleepState;
        if (hasDetailed && st === "ASLEEP") continue;
        if (st === "INBED" || st === "UNKNOWN") continue;
        const mins = Math.round((new Date(s.endDate) - new Date(s.startDate)) / 60000);
        if (mins <= 0) continue;
        if (st === "AWAKE")            awakeMinutes += mins;
        else if (isDeep(st))           { deepMinutes  += mins; sleepMinutes += mins; }
        else if (isREM(st))            { remMinutes   += mins; sleepMinutes += mins; }
        else if (st === "ASLEEPCORE")  sleepMinutes  += mins;
        else if (!hasDetailed && /asleep|core|unspecified/i.test(String(st))) sleepMinutes += mins;
      }
    } catch {}

    let log = {};
    try { log = JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch {}
    if (!log[todayKey]) log[todayKey] = { meals: { meal: [], drinks: [], supplements: [], whey: [], snacks: [] } };
    // Only persist as today's value when the sample is actually from today.
    // The Swift fallback can return 72h-old data; writing that into today's
    // slot would corrupt the 14-day baseline used by readiness deltas.
    if (rhrFresh && rhr > 0) { log[todayKey].restingHeartRate     = rhr; log[todayKey].restingHeartRateAt     = rhrAt; }
    if (hrvFresh && hrv > 0) { log[todayKey].heartRateVariability = hrv; log[todayKey].heartRateVariabilityAt = hrvAt; }
    // Non-fresh fallback (most recent reading in the Swift 72h window) goes
    // into a separate slot so the card can show "RHR · stale 1d ago" in the
    // morning instead of a blank "—" + a false "permissions broken" banner.
    // Deliberately NOT written to restingHeartRate/heartRateVariability so
    // the 14-day rolling baseline (readiness deltas) stays clean.
    if (rhr > 0 && !rhrFresh) { log[todayKey].restingHeartRateRecent     = rhr; log[todayKey].restingHeartRateRecentAt     = rhrAt; }
    if (hrv > 0 && !hrvFresh) { log[todayKey].heartRateVariabilityRecent = hrv; log[todayKey].heartRateVariabilityRecentAt = hrvAt; }
    if (sleepMinutes > 0) {
      log[todayKey].sleepMinutes = sleepMinutes;
      log[todayKey].deepMinutes  = deepMinutes;
      log[todayKey].remMinutes   = remMinutes;
      log[todayKey].awakeMinutes = awakeMinutes;
    }

    // Best-effort: pull the slow-moving fitness + early-warning signals so
    // the Readiness AI sees VO2max / wrist temp / walking HR even before
    // CalorieCounter has done a full sync. Failures are silent — the
    // existing fields above already carry the bulk of the readiness signal.
    try {
      const adv = await GhidhaHealth.readAdvancedSignals();
      if (adv) {
        if (adv.vo2Max          > 0) { log[todayKey].vo2Max          = parseFloat(Number(adv.vo2Max).toFixed(1)); if (adv.vo2MaxAt) log[todayKey].vo2MaxAt = adv.vo2MaxAt; }
        if (Number.isFinite(adv.vo2MaxDelta30d)  && adv.vo2MaxDelta30d  !== 0)
          log[todayKey].vo2MaxDelta30d  = parseFloat(Number(adv.vo2MaxDelta30d).toFixed(2));
        if (Number.isFinite(adv.wristTempDeltaC) && adv.wristTempDeltaC !== 0) {
          log[todayKey].wristTempDeltaC = parseFloat(Number(adv.wristTempDeltaC).toFixed(2));
          if (adv.wristTempDeltaAt) log[todayKey].wristTempDeltaAt = adv.wristTempDeltaAt;
        }
        if (adv.walkingHrAvg    > 0) { log[todayKey].walkingHrAvg    = Math.round(adv.walkingHrAvg); if (adv.walkingHrAvgAt) log[todayKey].walkingHrAvgAt = adv.walkingHrAvgAt; }
        if (adv.flightsClimbed  > 0) log[todayKey].flightsClimbed  = Math.round(adv.flightsClimbed);
        if (adv.mindfulMinutes  > 0) log[todayKey].mindfulMinutes  = Math.round(adv.mindfulMinutes);
      }
    } catch {}

    localStorage.setItem("calorieLog", JSON.stringify(log));
    try { window.dispatchEvent(new StorageEvent("storage", { key: "calorieLog" })); } catch {}
    return true;
  } catch {
    return false;
  }
}

// Stable job key — readiness has a single "current" reading so the
// background job runner can de-dupe across mounts.
const READINESS_JOB_KEY = "hayahReadiness";

export default function ReadinessCard({ showHeader = true, headerRight = null, topAccessory = null, refreshSignal = 0 }) {
  const hasGeminiKey = !!getGeminiKey();
  const [readiness, setReadiness] = useState(() => loadReadinessCache());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [steve, setSteve] = useState(false);
  const [expandedDriver, setExpandedDriver] = useState(null); // index of expanded chip, or null
  const [expandedRegion, setExpandedRegion] = useState(null); // region name expanded, or null
  const [evidenceOpen, setEvidenceOpen] = useState(false);    // raw-signal "Evidence" disclosure
  const [feel, setFeelState] = useState(() => loadFeel());    // subjective read — downward-only override
  // Persist the feel + a context snapshot onto today's history so the
  // objective-vs-subjective gap can be learned over time.
  const setFeel = (f) => {
    const next = feel === f ? null : f;
    saveFeel(next); setFeelState(next);
    try {
      const inp = computeReadinessInputs(readAllData());
      const mr = inp?.training?.muscleRecency || {};
      let legDays = null;
      for (const [m, d] of Object.entries(mr)) {
        if (/leg|quad|ham|glute|calf|calv/i.test(m)) legDays = legDays == null ? d : Math.min(legDays, d);
      }
      recordFeel(next, {
        objScore: readiness?.score ?? null,
        objBand:  readiness?.scoreBand || readiness?.classification || null,
        sleepH:   inp?.sleep?.totalHours ?? null,
        hrvDelta: inp?.hrv?.deltaPct ?? null,
        recH:     inp?.training?.hoursSinceLastSession ?? null,
        legsRecentH: legDays != null ? Math.round(legDays * 24) : null,
      });
    } catch {}
  };

  // Subscribe to the shared background job so the in-flight state and
  // freshly-cached result survive a screen leave + return. The callback
  // fires once immediately at registration, so even if the job
  // completed between the user navigating away and back, the UI picks
  // up the new cache on the next render.
  useEffect(() => {
    const sync = () => {
      setLoading(isInFlight(READINESS_JOB_KEY));
      setError(lastError(READINESS_JOB_KEY));
      if (!isInFlight(READINESS_JOB_KEY)) {
        try {
          const cached = loadReadinessCache();
          if (cached) setReadiness(cached);
        } catch {}
      }
    };
    return subscribe(READINESS_JOB_KEY, sync);
  }, []);

  // Daily morning-check notification — schedule once on mount. The
  // anomaly notification is fired on-demand from the effect below.
  useEffect(() => { initReadinessAlerts(); }, []);

  // Push a confounder anomaly notification whenever the inputs match
  // the illness or dehydration pattern. Idempotent per day per kind so
  // re-opening the card doesn't spam. Runs whenever the rendered card
  // changes (i.e. fresh data loaded or signals synced).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inp = computeReadinessInputs(readAllData());
        if (cancelled) return;
        if (inp?.confounderAlert) {
          await fireConfounderAlertIfNew(inp.confounderAlert);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
    // Re-run whenever readiness state changes (which happens after a
    // sync / generate cycle) — these are the only times new HealthKit
    // data has landed in calorieLog.
  }, [readiness]);

  const generate = useCallback((steveToday = false) => {
    if (isInFlight(READINESS_JOB_KEY)) return;
    runJob(READINESS_JOB_KEY, async () => {
      await syncReadinessSignals();
      const allData = readAllData();
      // generateReadiness writes to the readiness cache itself (via
      // saveReadinessCache) — we just need the call to complete, even
      // if this component is gone by then.
      await generateReadiness(allData, { steveToday });
    });
  }, []);

  // External refresh trigger (e.g. pull-to-refresh on the parent scroll
  // container). Bumping refreshSignal kicks off a new generate cycle
  // without exposing the ↻ button when the parent owns the gesture.
  const firstRefreshRef = useRef(true);
  useEffect(() => {
    if (firstRefreshRef.current) { firstRefreshRef.current = false; return; }
    generate(steve);
  }, [refreshSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hoisted hooks — must sit ABOVE the no-key early return below so
  // React sees the same hook order on every render (Rules of Hooks).
  const driversKey = (readiness?.keyDrivers || []).join("|") + (readiness?.generatedAt || "");
  const lastDriversKey = useRef(driversKey);

  if (!hasGeminiKey) {
    return (
      <div style={{
        background: "rgba(95,209,185,0.06)", border: "1px solid rgba(95,209,185,0.2)",
        borderRadius: 14, padding: "16px 18px",
      }}>
        <div style={{ fontFamily: FM, fontSize: 10, color: C.teal, letterSpacing: "1.6px", textTransform: "uppercase", marginBottom: 8 }}>
          Readiness
        </div>
        <div style={{ fontFamily: FS, fontSize: 13.5, color: C.ink, lineHeight: 1.6 }}>
          Add a Gemini API key in <strong>Hayah → AI Coach → Settings</strong> to unlock readiness scoring.
        </div>
      </div>
    );
  }

  // The objective band, then capped by the user's subjective feel (downward
  // only). The whole card recolours to the feel-adjusted band.
  const objectiveCls = readiness?.classification;
  const feelCap = capByFeel(objectiveCls, feel);
  const cls = feelCap.band;
  const cc  = cls === "HARD"     ? C.green
            : cls === "MEDIUM"   ? C.teal
            : cls === "EASY"     ? C.amber
            : cls === "REST"     ? C.coral
            : cls === "RECOVERY" ? C.teal   // post-workout — same calm teal, distinct copy
            : C.teal;

  // (driversKey + lastDriversKey hoisted above the no-key early return
  // for Rules-of-Hooks compliance.)
  if (lastDriversKey.current !== driversKey) {
    lastDriversKey.current = driversKey;
    if (expandedDriver !== null) setExpandedDriver(null);
  }

  // Render markdown-ish **bold** spans inside the glossary body. Tiny
  // local helper so we don't pull in a markdown lib for one feature.
  // Highlighted spans get a marker-pen treatment — accent-tinted background
  // + accent-tinted text + heavy weight so they pop against the prose for
  // quick scanning.
  function renderInline(text) {
    const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**")) {
        return (
          <strong
            key={i}
            style={{
              color: cc,
              fontWeight: 800,
              background: cc + "1c",
              padding: "0 4px",
              borderRadius: 4,
              whiteSpace: "nowrap",
            }}
          >{p.slice(2, -2)}</strong>
        );
      }
      return <React.Fragment key={i}>{p}</React.Fragment>;
    });
  }

  return (
    <div>
      {showHeader && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, padding: "0 2px", gap: 8 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <span style={{ fontFamily: FM, fontSize: 10, color: cc, letterSpacing: "1.6px", textTransform: "uppercase", fontWeight: 600 }}>
              Readiness
            </span>
            <button
              onClick={() => { try { hapticTap(); } catch {} generate(steve); }}
              disabled={loading}
              aria-label={readiness ? "Refresh readiness" : "Generate readiness"}
              style={{
                width: 24, height: 24, padding: 0,
                borderRadius: 999, border: `1px solid ${cc}40`,
                background: loading ? "transparent" : cc + "12",
                color: cc, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, lineHeight: 1, fontFamily: FM,
                opacity: loading ? 0.5 : 1,
              }}
            >{loading ? "…" : "↺"}</button>
          </span>
          {headerRight || (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {readiness?.generatedAt && (
                <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: "0.8px" }}>
                  {new Date(readiness.generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              <AICostBadge feature="readiness" prefix="·" fontSize={8} />
            </span>
          )}
        </div>
      )}
      {/* When the parent renders its own header (e.g. HayahAIInsights),
          surface the cost above the card itself so it's still visible. */}
      {!showHeader && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6, padding: "0 2px" }}>
          <AICostBadge feature="readiness" prefix="last gen ·" fontSize={8} />
        </div>
      )}
      <div style={{
        background: cc + "09", borderRadius: 14, padding: "14px 16px",
        border: `1px solid ${cc}2a`,
      }}>
        {loading ? (
          <AIAnalysingPipeline
            label="Analysing readiness"
            sources={[
              "Sleep (last 7d)",
              "HRV / resting HR",
              "Workouts (28d)",
              "Soreness & RPE",
              "Nutrition (14d)",
            ]}
            accent="teal"
          />
        ) : readiness ? (
          <>
            {topAccessory && (
              <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${cc}22` }}>
                {topAccessory}
              </div>
            )}
            {/* Score gauge — the continuous 0–100 readiness number, its band,
                trend vs yesterday, and confidence. Deterministic (readinessScore). */}
            {(() => {
              const sc = readiness.score;
              const hist = (() => { try { return loadReadinessHistory(); } catch { return []; } })();
              const prev = hist.find(h => h.date !== readiness.date && h.score != null);
              const delta = (sc != null && prev?.score != null) ? sc - prev.score : null;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                  <div style={{ width: 72, height: 72, flexShrink: 0, borderRadius: 999, border: `2px solid ${cc}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 16px ${cc}33`, background: cc + "0e" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: FM, fontSize: 24, fontWeight: 800, color: cc, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{sc != null ? sc : "—"}</div>
                      <div style={{ fontFamily: FM, fontSize: 7.5, color: C.faint, letterSpacing: "1px" }}>/ 100</div>
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: FM, fontSize: 13, color: cc, fontWeight: 800, letterSpacing: "1.4px", textTransform: "uppercase" }}>{cls}</span>
                      {delta != null && Math.abs(delta) >= 1 && (
                        <span style={{ fontFamily: FM, fontSize: 10, color: delta > 0 ? (C.green || C.teal) : C.coral, fontWeight: 700 }}>{delta > 0 ? "↑" : "↓"} {delta > 0 ? "+" : ""}{delta} vs yesterday</span>
                      )}
                    </div>
                    {readiness.confidence && (
                      <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "0.6px", marginTop: 3 }}>{readiness.confidence} confidence · {readiness.contributions?.length || 0} signals</div>
                    )}
                    {readiness.cappedFromScore && Array.isArray(readiness.gates) && readiness.gates.length > 0 ? (
                      <div style={{ fontFamily: FS, fontSize: 11, color: C.coral, marginTop: 4, lineHeight: 1.45 }}>
                        Recovered ({readiness.score}) but capped to {cls} — {readiness.gates[0].reason}
                      </div>
                    ) : readiness.toNextBand ? (
                      <div style={{ fontFamily: FS, fontSize: 11, color: C.faint, marginTop: 4 }}>+{readiness.toNextBand.points} pts → {readiness.toNextBand.band}</div>
                    ) : null}
                  </div>
                  <button
                    onClick={() => { setSteve(v => !v); try { hapticHayahToggle(); } catch {} }}
                    style={{ alignSelf: "flex-start", padding: "4px 10px", borderRadius: 8, border: `1px solid ${steve ? C.amber + "55" : "rgba(255,255,255,0.12)"}`, background: steve ? C.amber + "18" : "transparent", color: steve ? C.amber : C.faint, fontFamily: FM, fontSize: 9, letterSpacing: "0.8px", textTransform: "uppercase", cursor: "pointer", flexShrink: 0 }}>
                    Steve {steve ? "▲" : "○"}
                  </button>
                </div>
              );
            })()}
            {readiness.cappedNote && (
              <div style={{ marginBottom: 10, fontFamily: FS, fontSize: 11.5, color: C.coral, lineHeight: 1.5 }}>{readiness.cappedNote}</div>
            )}

            {/* Subjective feel — your own read, overrides the sensors downward.
                Hidden in RECOVERY (the session's already done). */}
            {objectiveCls !== "RECOVERY" && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 7 }}>How do you feel?</div>
                {(() => {
                  const hint = (() => {
                    try {
                      const inp = computeReadinessInputs(readAllData());
                      const mr = inp?.training?.muscleRecency || {};
                      let legDays = null;
                      for (const [m, d] of Object.entries(mr)) { if (/leg|quad|ham|glute|calf|calv/i.test(m)) legDays = legDays == null ? d : Math.min(legDays, d); }
                      return preEmptiveFeelHint(loadReadinessHistory(), {
                        sleepH: inp?.sleep?.totalHours, hrvDelta: inp?.hrv?.deltaPct,
                        recH: inp?.training?.hoursSinceLastSession,
                        legsRecentH: legDays != null ? Math.round(legDays * 24) : null,
                      });
                    } catch { return null; }
                  })();
                  return hint ? <div style={{ marginBottom: 7, fontFamily: FS, fontSize: 11, color: C.amber, lineHeight: 1.45 }}>{hint}</div> : null;
                })()}
                <div style={{ display: "flex", gap: 6 }}>
                  {FEEL_OPTIONS.map(o => {
                    const on = feel === o.key;
                    return (
                      <button key={o.key} onClick={() => { try { hapticTap(); } catch {} setFeel(o.key); }}
                        style={{ flex: 1, padding: "8px 4px", borderRadius: 10, border: `1px solid ${on ? cc : "rgba(255,255,255,0.12)"}`, background: on ? cc + "1e" : "transparent", color: on ? cc : C.faint, fontFamily: FM, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                        <span style={{ fontSize: 16, lineHeight: 1 }}>{o.emoji}</span>
                        <span style={{ fontSize: 8.5, letterSpacing: "0.4px", textTransform: "uppercase", fontWeight: 700 }}>{o.label}</span>
                      </button>
                    );
                  })}
                </div>
                {feelCap.capped && (
                  <div style={{ marginTop: 8, fontFamily: FS, fontSize: 11.5, color: cc, lineHeight: 1.45 }}>{feelCap.note}</div>
                )}
                {feel && (() => {
                  const c = (loadReadinessHistory() || []).filter(e => e.feel).length;
                  return (
                    <div style={{ marginTop: 6, fontFamily: FM, fontSize: 9.5, color: C.teal, letterSpacing: "0.4px", display: "flex", alignItems: "center", gap: 5 }}>
                      <span>✓</span>
                      <span>Logged{c > 0 ? ` · ${c} feel-day${c === 1 ? "" : "s"} recorded` : ""}{c < 5 ? ` · ${5 - c} more to spot your pattern` : ""}</span>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* WHY — each factor's contribution to the score (deterministic) */}
            {Array.isArray(readiness.contributions) && readiness.contributions.length > 0 && (() => {
              const maxAbs = Math.max(...readiness.contributions.map(c => Math.abs(c.points)), 1);
              return (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 7 }}>Why</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {readiness.contributions.map((c, i) => {
                      // A factor flagged as a gate concern is shown red regardless
                      // of its (possibly positive) point contribution — it's the
                      // reason you're not cleared higher.
                      const pos = c.points >= 0;
                      const tone = c.concern ? C.coral : (pos ? (C.green || C.teal) : C.coral);
                      const w = Math.round(Math.abs(c.points) / maxAbs * 100);
                      const gate = (readiness.gates || []).find(g => g.factorKey === c.key);
                      return (
                        <div key={i}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontFamily: FM, fontSize: 10.5, color: c.concern ? C.coral : C.faint, width: 78, flexShrink: 0 }}>
                              {c.concern ? "⚠ " : ""}{c.label}
                            </span>
                            <div style={{ flex: 1, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.06)", position: "relative", overflow: "hidden" }}>
                              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${w}%`, background: tone, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontFamily: FM, fontSize: 10.5, color: tone, fontWeight: 700, width: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pos ? "+" : ""}{c.points}</span>
                          </div>
                          {gate && (
                            <div style={{ fontFamily: FS, fontSize: 10, color: C.coral, marginLeft: 86, marginTop: 2, lineHeight: 1.4 }}>{gate.reason}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* WHAT LIFTS IT — forward-looking single-lever projections */}
            {Array.isArray(readiness.whatLifts) && readiness.whatLifts.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 7 }}>What lifts it</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {readiness.whatLifts.map((wl, i) => (
                    <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 999, border: `1px solid ${C.teal}40`, background: C.teal + "10" }}>
                      <span style={{ fontFamily: FS, fontSize: 11.5, color: C.ink }}>{wl.label}</span>
                      <span style={{ fontFamily: FM, fontSize: 10, color: C.teal, fontWeight: 800 }}>→ {wl.resultScore}{wl.tomorrow ? " tmrw" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LEARNING — readiness score trend + yesterday→today call */}
            {(() => {
              const hist = (() => { try { return loadReadinessHistory(); } catch { return []; } })();
              const scores = hist.filter(h => h.score != null).slice(0, 14).reverse().map(h => h.score);
              if (scores.length < 2) return null;
              const min = Math.min(...scores), max = Math.max(...scores);
              const span = Math.max(1, max - min);
              const W = 200, H = 28;
              const pts = scores.map((s, i) => `${(i / (scores.length - 1)) * W},${(H - ((s - min) / span) * H).toFixed(1)}`).join(" ");
              const prev = hist.find(h => h.date !== readiness.date && h.classification);
              return (
                <div style={{ marginBottom: 12, padding: "10px 12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10 }}>
                  <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 6 }}>Learning</div>
                  <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block", height: 28 }}>
                    <polyline points={pts} fill="none" stroke={cc} strokeWidth="1.5" />
                  </svg>
                  <div style={{ fontFamily: FS, fontSize: 11, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>
                    {scores.length}-day readiness trend{prev ? <> · yesterday <b style={{ color: C.ink }}>{prev.classification}{prev.score != null ? ` (${prev.score})` : ""}</b> → today <b style={{ color: cc }}>{cls}{readiness.score != null ? ` (${readiness.score})` : ""}</b></> : null}
                  </div>
                  {/* Learned objective-vs-feel gap */}
                  {(() => {
                    const g = analyzeFeelGap(hist);
                    if (g.ready) {
                      return <div style={{ fontFamily: FS, fontSize: 11, color: C.faint, marginTop: 7, paddingTop: 7, borderTop: "1px solid rgba(255,255,255,0.06)", lineHeight: 1.5 }}>🧠 {g.summary}</div>;
                    }
                    if (g.count >= 1) {
                      return <div style={{ fontFamily: FS, fontSize: 10.5, color: C.faint, marginTop: 7, paddingTop: 7, borderTop: "1px solid rgba(255,255,255,0.06)", lineHeight: 1.5, opacity: 0.8 }}>Logging how you feel — {g.count}/5 days. After {5 - g.count} more I'll spot your objective-vs-feel pattern.</div>;
                    }
                    return null;
                  })()}
                </div>
              );
            })()}
            {/* Confounder anomaly banner — rendered above everything
                else when the inputs match the illness or dehydration
                pattern. Same alert also fires a local notification so
                it persists in Notification Center. */}
            {(() => {
              const inp = (() => { try { return computeReadinessInputs(readAllData()); } catch { return null; } })();
              const a = inp?.confounderAlert;
              if (!a) return null;
              const isIll = a.kind === 'illness';
              const tone  = isIll ? C.coral : C.amber;
              return (
                <div style={{
                  marginBottom: 10, padding: "10px 12px",
                  background: `${tone}14`, border: `1px solid ${tone}55`,
                  borderRadius: 8, display: "flex", flexDirection: "column", gap: 4,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13, lineHeight: 1 }}>{isIll ? "⚠️" : "💧"}</span>
                    <span style={{ fontFamily: FM, fontSize: 10, color: tone, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      {a.title}
                    </span>
                  </div>
                  <div style={{ fontFamily: FS, fontSize: 12, color: C.ink, lineHeight: 1.5 }}>{a.detail}</div>
                </div>
              );
            })()}
            {/* Pre-workout fuel chip — single traffic-light read on
                whether the body has carbs + a recent meal on board.
                Shown above the muscle readiness so the answer to "can
                I train hard right now?" is reachable in one glance. */}
            {(() => {
              const inp = (() => { try { return computeReadinessInputs(readAllData()); } catch { return null; } })();
              const f = inp?.nutrition?.fuelState;
              if (!f) return null;
              const map = {
                fuelled: { emoji: "🟢", label: "Fuelled", tone: C.green || C.teal,
                           desc: `${inp.nutrition.todayCarbs}g carbs · last meal ${inp.nutrition.hoursSinceLastMeal != null ? `${inp.nutrition.hoursSinceLastMeal}h ago` : "today"}` },
                light:   { emoji: "🟡", label: "Light",   tone: C.amber,
                           desc: `${inp.nutrition.todayCarbs}g carbs${inp.nutrition.hoursSinceLastMeal != null ? ` · ${inp.nutrition.hoursSinceLastMeal}h since last meal` : ""}` },
                fasted:  { emoji: "🔴", label: "Fasted",  tone: C.coral,
                           desc: inp.nutrition.hoursSinceLastMeal != null ? `${inp.nutrition.hoursSinceLastMeal}h since last meal` : "no meals logged today" },
              };
              const m = map[f]; if (!m) return null;
              return (
                <div style={{
                  marginBottom: 10, padding: "8px 12px",
                  background: `${m.tone}10`, border: `1px solid ${m.tone}33`,
                  borderRadius: 8, display: "flex", alignItems: "center", gap: 10,
                }}>
                  <span style={{ fontSize: 14, lineHeight: 1 }}>{m.emoji}</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: FM, fontSize: 10, color: m.tone, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      Fuel · {m.label}
                    </span>
                    <span style={{ fontFamily: FS, fontSize: 11.5, color: C.faint, lineHeight: 1.4 }}>{m.desc}</span>
                  </div>
                </div>
              );
            })()}
            {/* Today's session summary — only when classification is
                RECOVERY. Surfaces the workout the verdict is reasoning
                about so the user can sanity-check it without leaving
                the card. */}
            {cls === "RECOVERY" && (() => {
              const inp = (() => { try { return computeReadinessInputs(readAllData()); } catch { return null; } })();
              const w = inp?.training?.lastWorkout;
              if (!w) return null;
              const bits = [];
              if (w.durationMin)   bits.push({ l: "DURATION",  v: `${w.durationMin}m` });
              if (w.totalSets)     bits.push({ l: "SETS",      v: String(w.totalSets) });
              if (w.totalVolumeKg) bits.push({ l: "VOLUME",    v: `${w.totalVolumeKg.toLocaleString()} kg` });
              if (w.maxHr)         bits.push({ l: "MAX HR",    v: `${w.maxHr} bpm` });
              if (w.avgHr)         bits.push({ l: "AVG HR",    v: `${w.avgHr} bpm` });
              if (w.activeKcal)    bits.push({ l: "ACTIVE",    v: `${w.activeKcal} kcal` });
              if (w.prCount > 0)   bits.push({ l: "PRs",       v: String(w.prCount) });
              if (w.hoursAgo != null) bits.push({ l: "ENDED",  v: `${w.hoursAgo}h ago` });
              if (bits.length === 0) return null;
              return (
                <div style={{ marginBottom: 10, padding: "10px 12px", background: cc + "0a", border: `1px solid ${cc}33`, borderRadius: 8 }}>
                  <div style={{ fontFamily: FM, fontSize: 9, color: cc, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 6 }}>
                    Today's session{w.name ? ` · ${w.name}` : ""}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                    {bits.map((b, i) => (
                      <div key={i} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: "0.6px", opacity: 0.7 }}>{b.l}</span>
                        <span style={{ fontFamily: FM, fontSize: 12, color: C.ink, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{b.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {cls !== "RECOVERY" && (() => {
              // Confidence / convergence — deterministic, no AI. Counts how
              // many independent objective markers point the SAME way as the
              // verdict. Single-day HRV is noisy; agreement ACROSS markers is
              // not — so convergence is the best proxy for how much to trust
              // the call. Pure read of the same inputs the AI scored, so it
              // can never contradict the classification.
              //
              // Skipped for RECOVERY because its agreement framing
              // (recovery-needed vs go-train binary) doesn't apply once the
              // session is already done.
              const inp = (() => { try { return computeReadinessInputs(readAllData()); } catch { return null; } })();
              if (!inp) return null;
              const markers = [];
              const add = (label, status) => { if (status) markers.push({ label, status }); };
              const hrvD = inp.hrv?.deltaPct;
              if (inp.hrv?.today && hrvD != null)         add("HRV",           hrvD <= -10 ? "adverse" : "ok");
              const rhrD = inp.rhr?.deltaBpm;
              if (inp.rhr?.today && rhrD != null)         add("RHR",           rhrD >= 4 ? "adverse" : "ok");
              const slp = inp.sleep?.totalHours;
              if (slp != null && slp > 0)                 add("Sleep",         slp < 6.5 ? "adverse" : "ok");
              const acwr = inp.training?.acwr;
              if (acwr != null && acwr > 0)               add("ACWR",          (acwr > 1.5 || acwr < 0.8) ? "adverse" : "ok");
              const trend = inp.hrv?.trend3vs14Pct;
              if (trend != null)                          add("HRV trend",     trend <= -10 ? "adverse" : "ok");
              const rec = inp.training?.hoursSinceLastSession;
              if (rec != null && rec > 0)                 add("Recovery time", rec < 24 ? "adverse" : "ok");
              if (markers.length < 2) return null;

              const needsRecovery = cls === "REST" || cls === "EASY";
              const agree = markers.filter(m => (m.status === "adverse") === needsRecovery).length;
              const total = markers.length;
              const ratio = agree / total;
              const level = (total >= 3 && ratio >= 0.7) ? "HIGH"
                          : ratio >= 0.5 ? "MODERATE" : "LOW";
              const lc = level === "HIGH" ? C.teal : level === "MODERATE" ? C.amber : C.coral;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 10, padding: "7px 10px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}>
                  <span style={{ fontFamily: FM, fontSize: 9, color: lc, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>{level} confidence</span>
                  <span style={{ fontFamily: FM, fontSize: 9.5, color: C.faint }}>· {agree} of {total} markers agree</span>
                  <span style={{ display: "flex", gap: 3, marginLeft: "auto" }}>
                    {markers.map((m, i) => {
                      const agrees = (m.status === "adverse") === needsRecovery;
                      return <span key={i} title={m.label} style={{ width: 6, height: 6, borderRadius: 6, background: agrees ? cc : "rgba(255,255,255,0.18)", boxShadow: agrees ? `0 0 4px ${cc}` : "none" }} />;
                    })}
                  </span>
                </div>
              );
            })()}
            {(() => {
              // Per-region readiness — deterministic, scopes the systemic
              // verdict DOWN per region based on how recently it was
              // trained. Surfaces "legs need a day, upper body is green".
              const inp = (() => { try { return computeReadinessInputs(readAllData()); } catch { return null; } })();
              const recency = inp?.training?.muscleRecency;
              if (!recency || Object.keys(recency).length === 0) return null;
              const regions = computeRegionReadiness(cls, recency);
              const colorFor = (rc) => rc === "HARD" ? C.green : rc === "MEDIUM" ? C.teal : rc === "EASY" ? C.amber : C.coral;
              const open = expandedRegion ? regions.find(r => r.region === expandedRegion) : null;
              return (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 6 }}>Muscle readiness</div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {regions.map((r) => {
                      const rcc = colorFor(r.cls);
                      const isOpen = r.region === expandedRegion;
                      return (
                        <button key={r.region}
                          onClick={() => { setExpandedRegion(isOpen ? null : r.region); try { hapticTap(); } catch {} }}
                          style={{
                            flex: "1 1 0", minWidth: 64, display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                            padding: "7px 6px", borderRadius: 9, cursor: "pointer",
                            background: rcc + (isOpen ? "22" : "10"), border: `1px solid ${rcc}${isOpen ? "66" : "2a"}`,
                          }}>
                          <span style={{ fontFamily: FS, fontSize: 11, fontWeight: 700, color: C.ink }}>{r.region}</span>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span style={{ width: 5, height: 5, borderRadius: 5, background: rcc, boxShadow: `0 0 5px ${rcc}` }} />
                            <span style={{ fontFamily: FM, fontSize: 8.5, fontWeight: 700, color: rcc, letterSpacing: "0.6px" }}>{r.cls}</span>
                          </span>
                          <span style={{ fontFamily: FM, fontSize: 8, color: C.faint }}>
                            {r.daysAgo == null ? "fresh" : r.daysAgo === 0 ? "today" : `${r.daysAgo}d ago`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {open && (
                    <div style={{ marginTop: 6, padding: "7px 10px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, fontFamily: FS, fontSize: 12, color: C.ink, lineHeight: 1.5 }}>
                      {open.reason}
                      {open.cappedByRecovery && <span style={{ color: C.faint }}> {" "}Capped below your {cls} systemic readiness by local recovery.</span>}
                    </div>
                  )}
                </div>
              );
            })()}
            {(() => {
              // Bullets: newline-separated. Legacy cached values are prose
              // sentences — split those on sentence boundaries so the bullet
              // layout still works on stale data.
              const raw = String(readiness.rationale || "").trim();
              if (!raw) return null;
              let bullets = raw.split(/\n+/).map(s => s.trim()).filter(Boolean);
              if (bullets.length === 1) {
                bullets = raw.split(/(?<=[.!?])\s+(?=[A-Z])/).map(s => s.trim()).filter(Boolean);
              }
              bullets = bullets.map(b => b.replace(/^[•\-*\d.\s]+/, "").trim()).filter(Boolean);
              if (bullets.length === 0) return null;
              return (
                <ul style={{ listStyle: "none", margin: "0 0 10px", padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                  {bullets.map((b, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontFamily: FS, fontSize: 13.5, color: C.ink, lineHeight: 1.55 }}>
                      <span style={{ color: cc, flexShrink: 0, fontWeight: 700, marginTop: 1 }}>•</span>
                      <span style={{ flex: 1 }}>{renderInline(b)}</span>
                    </li>
                  ))}
                </ul>
              );
            })()}
            {readiness.keyDrivers?.length > 0 && (() => {
              const drivers = readiness.keyDrivers;
              const explained = drivers.map(d => getDriverExplanation(d));
              const openExp = expandedDriver != null ? explained[expandedDriver] : null;
              return (
                <>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: openExp ? 6 : 10 }}>
                    {drivers.map((d, i) => {
                      const isOpen   = i === expandedDriver;
                      const hasExpl  = !!explained[i];
                      const baseStyle = {
                        fontFamily: FM, fontSize: 9, color: cc,
                        background: cc + (isOpen ? "26" : "10"),
                        border: `1px solid ${cc}${isOpen ? "66" : "28"}`,
                        borderRadius: 6, padding: "3px 8px",
                        letterSpacing: "0.5px",
                        opacity: hasExpl ? 0.95 : 0.55,
                        cursor: hasExpl ? "pointer" : "default",
                        display: "inline-flex", alignItems: "center", gap: 4,
                      };
                      if (!hasExpl) return <span key={i} style={baseStyle}>{d}</span>;
                      return (
                        <button
                          key={i}
                          onClick={() => setExpandedDriver(isOpen ? null : i)}
                          style={baseStyle}>
                          {d}
                          <span style={{ fontSize: 7, opacity: 0.6 }}>{isOpen ? "▾" : "ⓘ"}</span>
                        </button>
                      );
                    })}
                  </div>
                  {openExp && (
                    <div style={{
                      fontFamily: FS, fontSize: 11.5, color: C.faint, lineHeight: 1.55,
                      background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 8, padding: "10px 12px", marginBottom: 10,
                    }}>
                      <div style={{ fontFamily: FM, fontSize: 9, color: cc, letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700, marginBottom: 5 }}>
                        {openExp.title}
                      </div>
                      <div>{renderInline(openExp.body)}</div>
                    </div>
                  )}
                </>
              );
            })()}
            {/* Evidence — raw signals, cardio trend, baseline drift, ruled-out.
                Collapsed by default so the score → why → action lead the card. */}
            <button onClick={() => { try { hapticTap(); } catch {} setEvidenceOpen(o => !o); }}
                    style={{ background: "transparent", border: "none", color: C.faint, fontFamily: FM, fontSize: 9.5, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", padding: "2px 0", marginBottom: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {evidenceOpen ? "▾" : "▸"} Evidence · signals, trends, baselines
            </button>
            {evidenceOpen && (<>
            {/* Raw signals — lets the user verify HealthKit data is flowing
                even when the AI rationale focuses on other factors. */}
            {(() => {
              const inp = (() => { try { return computeReadinessInputs(readAllData()); } catch { return null; } })();
              if (!inp) return null;
              const dim  = "rgba(255,255,255,0.25)";
              const live = "rgba(255,255,255,0.85)";
              const fmt  = (v, unit) => v == null || v === 0 ? "—" : `${v}${unit}`;
              // Format the sample's recorded time as a freshness hint. If
              // the sample is from today, show HH:MM; otherwise show "Nd ago"
              // (caps at 30+d). Returns null when no timestamp is present.
              const ageOf = (iso) => {
                if (!iso) return null;
                const ts = Date.parse(iso); if (!Number.isFinite(ts)) return null;
                const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
                if (ts >= todayStart.getTime()) {
                  const d = new Date(ts);
                  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                }
                const days = Math.max(1, Math.round((Date.now() - ts) / 86400000));
                return days >= 30 ? "30+d ago" : `${days}d ago`;
              };
              const isStale = (iso) => {
                if (!iso) return false;
                const ts = Date.parse(iso); if (!Number.isFinite(ts)) return false;
                const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
                return ts < todayStart.getTime();
              };
              const rec = inp.recovery || {};
              const items = [
                { l: "HRV",        v: inp.hrv?.today ?? inp.hrv?.recent, u: "ms",  at: inp.hrv?.todayAt || inp.hrv?.recentAt },
                { l: "RHR",        v: inp.rhr?.today ?? inp.rhr?.recent, u: "bpm", at: inp.rhr?.todayAt || inp.rhr?.recentAt },
                { l: "Sleep",      v: inp.sleep?.totalHours,      u: "h" },
                { l: "Fuel today", v: inp.nutrition?.todayCalories, u: " kcal" },
                { l: "Carbs",      v: inp.nutrition?.todayCarbs,    u: "g" },
                { l: "Protein",    v: inp.nutrition?.todayProtein,  u: "g" },
              ];
              // Advanced Apple Health signals — only appended when actually
              // present so the grid stays clean on days/devices without an
              // Apple Watch reading. These feed the AI too (readiness.js);
              // surfacing them here lets the user verify the full data scope.
              const wt  = rec.wristTempDeltaC;
              if (wt != null && wt !== 0)
                items.push({ l: "Wrist temp", display: `${wt > 0 ? "+" : ""}${wt}°C`, at: rec.wristTempDeltaAt });
              if (rec.vo2Max != null && rec.vo2Max !== 0) {
                const d = rec.vo2MaxDelta30d;
                items.push({ l: "VO₂max", display: `${rec.vo2Max}${d ? ` (${d > 0 ? "+" : ""}${d})` : ""}`, at: rec.vo2MaxAt });
              }
              if (inp.activity?.walkingHrAvgBpm)
                items.push({ l: "Walking HR", v: inp.activity.walkingHrAvgBpm, u: "bpm", at: inp.activity.walkingHrAvgAt });
              if (rec.lastWorkoutHrRecoveryBpm)
                items.push({ l: "HR recov", v: rec.lastWorkoutHrRecoveryBpm, u: "bpm" });
              if (rec.mindfulMinutesToday)
                items.push({ l: "Mindful", v: rec.mindfulMinutesToday, u: "m" });
              // If HRV or RHR is missing but Sleep is present, HealthKit is
              // reachable but those specific data types weren't granted (or
              // the Watch hasn't recorded any) — surface a tap-to-fix link
              // that jumps straight to iOS Settings → Sirat where the user
              // can flip the per-type toggles.
              // "Missing" only when there's no reading at all in the 72h
              // window — not merely when today's overnight sample hasn't
              // landed yet. A stale-but-present value means HealthKit IS
              // flowing, so don't show the misleading permissions banner.
              const hrvMissing = !(inp.hrv?.today || inp.hrv?.recent);
              const rhrMissing = !(inp.rhr?.today || inp.rhr?.recent);
              const sleepOk    = !!inp.sleep?.totalHours;
              const showHealthFix = (hrvMissing || rhrMissing) && sleepOk;
              return (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: showHealthFix ? 6 : 10, padding: "8px 10px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}>
                    {items.map((it, i) => {
                      const txt   = it.display != null ? it.display : fmt(it.v, it.u);
                      const isDim = it.display == null && (it.v == null || it.v === 0);
                      const age   = it.at ? ageOf(it.at) : null;
                      const stale = it.at ? isStale(it.at) : false;
                      return (
                        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <span style={{ fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: "0.8px", textTransform: "uppercase", opacity: 0.7 }}>{it.l}</span>
                          <span style={{ fontFamily: FM, fontSize: 12, color: isDim ? dim : (stale ? C.amber : live), fontWeight: 600 }}>{txt}</span>
                          {age && !isDim && (
                            <span style={{ fontFamily: FM, fontSize: 8, color: stale ? C.amber : C.faint, opacity: stale ? 0.95 : 0.6, letterSpacing: "0.2px" }}>
                              {stale ? `stale · ${age}` : age}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {showHealthFix && (
                    <button
                      onClick={() => { try { hapticTap(); } catch {} openIOSSettings(); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        marginBottom: 10, padding: "7px 10px",
                        background: "rgba(255,184,77,0.08)",
                        border: "1px solid rgba(255,184,77,0.28)",
                        borderRadius: 8, cursor: "pointer",
                        width: "100%", textAlign: "left",
                        WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      <span style={{ color: "#ffb84d", fontSize: 13, lineHeight: 1 }}>⚠︎</span>
                      <span style={{ flex: 1, fontFamily: FS, fontSize: 11.5, color: "#ffb84d", lineHeight: 1.45, letterSpacing: "-0.005em" }}>
                        {hrvMissing && rhrMissing ? "HRV + RHR" : (hrvMissing ? "HRV" : "RHR")} not coming through. Tap to fix → <span style={{ opacity: 0.85 }}>Settings → Health → Data Access → Sirat → enable Heart Rate Variability + Resting Heart Rate.</span>
                      </span>
                      <span style={{ color: "#ffb84d", fontSize: 13, lineHeight: 1 }}>→</span>
                    </button>
                  )}
                </>
              );
            })()}
            {/* Cardio fitness — longitudinal trend from RHR + walking HR +
                HR recovery (+VO₂max). Turns the raw signals above into a
                direction the user can act on. */}
            {(() => {
              const cf = (() => { try { return computeCardioFitness(); } catch { return null; } })();
              if (!cf || !cf.hasData) return null;
              const tone = cf.verdict === "improving" ? C.green
                         : cf.verdict === "declining" ? C.coral
                         : C.teal;
              const arrow = cf.verdict === "improving" ? "↑" : cf.verdict === "declining" ? "↓" : "→";
              return (
                <div style={{ marginBottom: 10, padding: "9px 11px", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: cf.summary ? 4 : 0 }}>
                    <span style={{ fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: "0.8px", textTransform: "uppercase", opacity: 0.7 }}>Cardio fitness</span>
                    <span style={{ fontFamily: FM, fontSize: 11, fontWeight: 700, color: tone, letterSpacing: "0.4px", textTransform: "capitalize" }}>{arrow} {cf.verdict}</span>
                  </div>
                  {cf.summary && (
                    <div style={{ fontFamily: FM, fontSize: 9.5, color: C.faint, opacity: 0.85, letterSpacing: "0.2px" }}>
                      {cf.summary} <span style={{ opacity: 0.6 }}>· {cf.windowLabel}</span>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* 30-day HRV/RHR baseline drift — for each of the last 30
                days we compute that day's 14-day backward rolling avg
                and plot it as a sparkline. Catches gradual baseline
                creep (over-reaching) that day-to-day deltas can't see. */}
            {(() => {
              const log = (() => { try { return readAllData()?.calorieLog || {}; } catch { return {}; } })();
              const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              const DAYS = 30, WIN = 14;
              const series = [];
              for (let i = DAYS - 1; i >= 0; i--) {
                const anchor = new Date(); anchor.setDate(anchor.getDate() - i);
                let hrvSum = 0, hrvN = 0, rhrSum = 0, rhrN = 0;
                for (let k = 0; k < WIN; k++) {
                  const d = new Date(anchor); d.setDate(d.getDate() - k);
                  const v = log[fmt(d)];
                  const hv = v?.heartRateVariability;
                  const rv = v?.restingHeartRate;
                  if (Number.isFinite(hv) && hv > 0) { hrvSum += hv; hrvN++; }
                  if (Number.isFinite(rv) && rv > 0) { rhrSum += rv; rhrN++; }
                }
                series.push({
                  hrv: hrvN >= 3 ? hrvSum / hrvN : null,
                  rhr: rhrN >= 3 ? rhrSum / rhrN : null,
                });
              }
              const hrvVals = series.map(s => s.hrv).filter(v => v != null);
              const rhrVals = series.map(s => s.rhr).filter(v => v != null);
              if (hrvVals.length < 7 && rhrVals.length < 7) return null;
              const slopeOf = (vals) => {
                if (vals.length < 4) return null;
                const head = vals.slice(-7).reduce((s, v) => s + v, 0) / Math.min(7, vals.length);
                const tail = vals.slice(0, 7).reduce((s, v) => s + v, 0) / Math.min(7, vals.length);
                return head - tail; // positive = trending up
              };
              const hrvSlope = slopeOf(hrvVals);
              const rhrSlope = slopeOf(rhrVals);
              // Spark = inline mini-SVG line on 0..1 normalised range.
              const Spark = ({ vals, color }) => {
                if (!vals.some(v => v != null)) return null;
                const W = 220, H = 28, PAD = 2;
                const nums = vals.map(v => v == null ? null : v);
                const present = nums.filter(v => v != null);
                const lo = Math.min(...present), hi = Math.max(...present);
                const range = hi - lo || 1;
                const xs = nums.map((_, i) => PAD + (i * (W - 2 * PAD)) / Math.max(1, nums.length - 1));
                const ys = nums.map(v => v == null ? null : (H - PAD - ((v - lo) / range) * (H - 2 * PAD)));
                let path = "";
                let pen = false;
                for (let i = 0; i < nums.length; i++) {
                  if (ys[i] == null) { pen = false; continue; }
                  path += `${pen ? "L" : "M"}${xs[i].toFixed(1)},${ys[i].toFixed(1)} `;
                  pen = true;
                }
                return (
                  <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                    <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
                  </svg>
                );
              };
              const Row = ({ label, vals, slope, unit, goodIsDown = false }) => {
                if (vals.length < 4) return null;
                const dir = slope == null ? null : (slope > 0 ? "up" : slope < 0 ? "down" : "flat");
                const good = dir == null ? null : (goodIsDown ? dir === "down" : dir === "up");
                const color = good == null ? C.faint : (good ? C.teal : C.coral);
                const arrow = dir === "up" ? "▲" : dir === "down" ? "▼" : "—";
                const slopeStr = slope == null ? "—" : `${slope >= 0 ? "+" : ""}${slope.toFixed(1)}${unit}`;
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                      <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "0.8px", textTransform: "uppercase" }}>{label}</span>
                      <span style={{ fontFamily: FM, fontSize: 10, color, fontWeight: 700 }}>{arrow} {slopeStr}</span>
                    </div>
                    <Spark vals={vals} color={color} />
                  </div>
                );
              };
              return (
                <div style={{
                  marginBottom: 10, padding: "10px 12px",
                  background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 8, display: "flex", flexDirection: "column", gap: 8,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "1.2px", textTransform: "uppercase" }}>
                      Baseline drift · 30 days
                    </span>
                    <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, opacity: 0.6, letterSpacing: "0.6px" }}>
                      14d rolling avg
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 14 }}>
                    <Row label="HRV"  vals={series.map(s => s.hrv)} slope={hrvSlope} unit="ms" goodIsDown={false} />
                    <Row label="RHR"  vals={series.map(s => s.rhr)} slope={rhrSlope} unit="bpm" goodIsDown={true} />
                  </div>
                </div>
              );
            })()}
            {(() => {
              // "Ruled out" — confounder all-clears. Turns silent grid
              // values into meaning by naming the things that COULD have
              // explained away the signal but didn't (illness, dehydration,
              // poor conditioning). Deterministic; only shown when the data
              // actually supports the reassurance.
              const inp = (() => { try { return computeReadinessInputs(readAllData()); } catch { return null; } })();
              if (!inp) return null;
              const rec = inp.recovery || {};
              const clears = [];
              const wt = rec.wristTempDeltaC;
              if (wt != null && wt !== 0 && wt <= 0.30 && wt >= -0.20)
                clears.push("Wrist temp normal — reads as training fatigue, not illness or a heavy night");
              const ov = inp.body?.overnightDeltaKg;
              if (ov != null && ov > -1.5 && inp.hrv?.deltaPct != null && inp.hrv.deltaPct < 0)
                clears.push("Overnight weight stable — the HRV dip is real fatigue, not just dehydration");
              const hrRec = rec.lastWorkoutHrRecoveryBpm;
              if (hrRec != null && hrRec >= 25)
                clears.push(`Strong post-workout HR recovery (${hrRec} bpm) — your cardio system is coping well`);
              const vo2d = rec.vo2MaxDelta30d;
              if (vo2d != null && vo2d > 0.5)
                clears.push(`VO₂max trending up (+${vo2d}) — aerobic base is improving, not declining`);
              if (clears.length === 0) return null;
              return (
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 8, marginBottom: 10 }}>
                  <div style={{ fontFamily: FM, fontSize: 9, color: C.teal, letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Ruled out</div>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 5 }}>
                    {clears.map((c, i) => (
                      <li key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start", fontFamily: FS, fontSize: 12, color: C.faint, lineHeight: 1.5 }}>
                        <span style={{ color: C.teal, flexShrink: 0, fontWeight: 700, marginTop: 1 }}>✓</span>
                        <span style={{ flex: 1 }}>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            </>)}
            {readiness.nutritionAdvice && (() => {
              // Bullets: prompt asks the AI for newline-separated short
              // bullets (no prefix). Legacy cached values are prose with
              // sentence terminators — split those into pseudo-bullets so
              // the layout still works on stale data.
              const raw = String(readiness.nutritionAdvice || "").trim();
              let bullets = raw.split(/\n+/).map(s => s.trim()).filter(Boolean);
              if (bullets.length === 1) {
                bullets = raw.split(/(?<=[.!?])\s+(?=[A-Z])/).map(s => s.trim()).filter(Boolean);
              }
              // Strip any bullet prefix the model might add anyway.
              bullets = bullets.map(b => b.replace(/^[•\-*\d.\s]+/, "").trim()).filter(Boolean);
              if (bullets.length === 0) return null;
              return (
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 8, marginBottom: 10 }}>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                    {bullets.map((b, i) => (
                      <li key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontFamily: FS, fontSize: 12.5, color: C.faint, lineHeight: 1.5 }}>
                        <span style={{ color: cc, flexShrink: 0, fontWeight: 700, marginTop: 1 }}>•</span>
                        <span style={{ flex: 1 }}>{renderInline(b)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            {(() => {
              // "Next step" — verdict-specific, low-risk action + how to
              // verify the call tomorrow. Closes the loop: the card tells
              // you what the data says AND what to do about it.
              const steps = {
                HARD:     "Green light — train hard. Fuel properly and check tomorrow's HRV to confirm you absorbed the load.",
                MEDIUM:   "Moderate session is fine — get your work in but don't max out. Re-check HRV tomorrow before anything hard.",
                EASY:     "Keep it light today (zone-2 cardio, technique, mobility). Re-check HRV tomorrow — a rebound clears you for intensity.",
                REST:     "Rest, or keep it to a walk / mobility only — nothing that adds load. Re-check HRV tomorrow; a rebound toward baseline means you're clear to train.",
                RECOVERY: "Session done — protein within the next 90 min, hydrate, no second hard session today. Walk / mobility only. HRV + sleep tonight determine tomorrow's call.",
              };
              const txt = steps[cls];
              if (!txt) return null;
              return (
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 8 }}>
                  <div style={{ fontFamily: FM, fontSize: 9, color: cc, letterSpacing: "1px", textTransform: "uppercase", fontWeight: 700, marginBottom: 5 }}>Next step</div>
                  <div style={{ fontFamily: FS, fontSize: 12.5, color: C.ink, lineHeight: 1.55 }}>{txt}</div>
                </div>
              );
            })()}
          </>
        ) : error ? (
          <div style={{ fontFamily: FM, fontSize: 11, color: C.coral, marginBottom: 10, lineHeight: 1.6 }}>{error}</div>
        ) : (
          <div style={{ fontFamily: FM, fontSize: 12, color: C.faint, marginBottom: 10, lineHeight: 1.6 }}>
            Tap below to generate your readiness score from sleep, HRV, RHR, training load, and nutrition.
          </div>
        )}
        {/* Compact "last refreshed" line — the refresh action is now in the
            card header next to the Readiness label. */}
        {readiness?.generatedAt && (() => {
          const gen = new Date(readiness.generatedAt);
          const ageMs = Date.now() - gen.getTime();
          const ageMin = Math.round(ageMs / 60000);
          const ageHr  = Math.floor(ageMin / 60);
          const ageDay = Math.floor(ageHr / 24);
          const rel =
            ageMin < 1   ? "just now" :
            ageMin < 60  ? `${ageMin}m ago` :
            ageHr  < 24  ? `${ageHr}h ago` :
            ageDay < 7   ? `${ageDay}d ago` :
            null;
          const isToday = (() => {
            const n = new Date();
            return gen.getFullYear() === n.getFullYear() && gen.getMonth() === n.getMonth() && gen.getDate() === n.getDate();
          })();
          const stamp = isToday
            ? gen.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            : gen.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" }) + " · " +
              gen.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          return (
            <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: "0.06em" }}>
              Refreshed {stamp}{rel ? ` · ${rel}` : ""}
            </div>
          );
        })()}
        {!readiness && (
          <button
            onClick={() => { try { hapticTap(); } catch {} generate(steve); }}
            disabled={loading}
            style={{ padding: "7px 14px", borderRadius: 9, border: `1px solid ${cc}33`, background: loading ? "transparent" : cc + "10", color: cc, fontFamily: FM, fontSize: 9, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", cursor: "pointer", opacity: loading ? 0.5 : 1 }}>
            {loading ? "…" : "✦ Get Readiness"}
          </button>
        )}
      </div>
      <style>{`@keyframes hayah-blink-dot{0%,100%{opacity:1}50%{opacity:0.2}}`}</style>
    </div>
  );
}
